import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ChevronRight,
  Inbox as InboxIcon,
  Mail,
  MailOpen,
  Plus,
  RefreshCw,
  Search,
  SendHorizontal,
  Settings2,
  Trash2,
} from 'lucide-react';
import { api, dateTime } from '../lib/api';
import EmailThreadPanel from '../components/EmailThreadPanel';
import ManagementDeleteDialog from '../components/ManagementDeleteDialog';
import EmailComposeDialog from '../components/EmailComposeDialog';

const mailboxLabel=box=>box?.display_name||box?.email||'Mailbox';

export default function Inbox(){
  const[data,setData]=useState({messages:[],mailboxes:[]});
  const[selected,setSelected]=useState('');
  const[mailbox,setMailbox]=useState('');
  const[direction,setDirection]=useState('');
  const[query,setQuery]=useState('');
  const[error,setError]=useState('');
  const[busy,setBusy]=useState(false);
  const[deleteTarget,setDeleteTarget]=useState(null);
  const[composeOpen,setComposeOpen]=useState(false);

  const load=useCallback(async()=>{
    setBusy(true);
    setError('');
    try{
      const p=new URLSearchParams();
      if(mailbox)p.set('mailbox_id',mailbox);
      if(direction)p.set('direction',direction);
      if(query.trim())p.set('q',query.trim());
      p.set('limit','120');
      const body=await api(`/api/email/inbox?${p.toString()}`);
      setData(body);
      setSelected(current=>{
        if(current&&body.messages?.some(message=>message.id===current))return current;
        return body.messages?.[0]?.id||'';
      });
    }catch(e){
      setError(e.message);
    }finally{
      setBusy(false);
    }
  },[mailbox,direction,query]);

  useEffect(()=>{
    const timer=setTimeout(load,query?220:0);
    return()=>clearTimeout(timer);
  },[load,query]);

  const messages=data.messages||[];
  const mailboxes=data.mailboxes||[];
  const unread=useMemo(()=>messages.filter(message=>!message.is_read).length,[messages]);
  const inbound=useMemo(()=>messages.filter(message=>message.direction!=='outbound').length,[messages]);
  const outbound=useMemo(()=>messages.filter(message=>message.direction==='outbound').length,[messages]);
  const activeMailbox=mailboxes.find(item=>item.id===mailbox)||null;
  const selectedMessage=messages.find(message=>message.id===selected)||null;

  const syncNow=async()=>{
    setBusy(true);
    setError('');
    try{
      await api('/api/admin/partnerships/microsoft/sync',{
        method:'POST',
        headers:{'content-type':'application/json'},
        body:JSON.stringify(mailbox?{mailbox_id:mailbox}:{})
      });
      await load();
    }catch(e){
      setError(e.message);
      setBusy(false);
    }
  };

  const toggleAutoAck=async()=>{
    if(!activeMailbox)return;
    await api(`/api/email/mailboxes/${encodeURIComponent(activeMailbox.id)}`,{
      method:'PATCH',
      headers:{'content-type':'application/json'},
      body:JSON.stringify({customer_auto_ack_enabled:!Boolean(Number(activeMailbox.customer_auto_ack_enabled))})
    });
    await load();
  };

  const remove=async()=>{
    if(!deleteTarget)return;
    setBusy(true);
    setError('');
    try{
      await api(`/api/management/records/email_message/${encodeURIComponent(deleteTarget.id)}`,{
        method:'DELETE',
        headers:{'content-type':'application/json'}
      });
      setDeleteTarget(null);
      setSelected('');
      await load();
    }catch(e){
      setError(e.message);
    }finally{
      setBusy(false);
    }
  };

  const chooseFolder=next=>{
    setDirection(next);
    setSelected('');
  };

  return <div className="crm-inbox-page">
    <section className="mail-console">
      <header className="mail-command-bar">
      <div className="mail-command-title">
        <span className="mail-command-eyebrow">Customer Operations · Microsoft 365</span>
        <div className="mail-command-heading">
          <h1 className="govuk-heading-xl">Inbox</h1>
          <span className="mail-command-count">{unread} unread</span>
        </div>
        <p className="govuk-body">Read, send and manage customer email without leaving Customer Operations.</p>
      </div>
      <div className="mail-command-actions">
        {selectedMessage&&<button className="button mail-remove-action govuk-button" type="button" onClick={()=>setDeleteTarget(selectedMessage)} disabled={busy}>
          <Trash2 size={14}/>Remove
        </button>}
        <button className="button secondary govuk-button govuk-button--secondary" onClick={syncNow} disabled={busy}>
          <RefreshCw size={15} className={busy?'mail-spin':''}/>{busy?'Syncing…':'Sync Microsoft 365'}
        </button>
        <button className="button primary govuk-button" onClick={()=>setComposeOpen(true)} disabled={busy||!mailboxes.length}>
          <Plus size={15}/>New email
        </button>
      </div>
      </header>

      {error&&<div className="operation-notice error mail-console-notice">{error}</div>}

      <div className="mail-workspace">
      <aside className="mail-navigation" aria-label="Email folders and mailboxes">
        <div className="mail-navigation-section">
          <span className="mail-navigation-label">Folders</span>
          <button className={!direction?'active':''} onClick={()=>chooseFolder('')}>
            <Mail size={17}/><span>Inbox & sent</span><strong>{messages.length}</strong>
          </button>
          <button className={direction==='inbound'?'active':''} onClick={()=>chooseFolder('inbound')}>
            <InboxIcon size={17}/><span>Inbox</span><strong>{inbound}</strong>
          </button>
          <button className={direction==='outbound'?'active':''} onClick={()=>chooseFolder('outbound')}>
            <SendHorizontal size={17}/><span>Sent</span><strong>{outbound}</strong>
          </button>
        </div>

        <div className="mail-navigation-section">
          <div className="mail-navigation-label-row">
            <span className="mail-navigation-label">Mailboxes</span>
            <span>{mailboxes.length}</span>
          </div>
          <button className={!mailbox?'active':''} onClick={()=>{setMailbox('');setSelected('')}}>
            <Archive size={17}/><span>All mailboxes</span>
          </button>
          <div className="mailbox-list">
            {mailboxes.map(box=><button
              key={box.id}
              className={mailbox===box.id?'active':''}
              onClick={()=>{setMailbox(box.id);setSelected('')}}
              title={box.email}
            >
              <span className="mailbox-avatar">{String(mailboxLabel(box)).slice(0,1).toUpperCase()}</span>
              <span className="mailbox-copy">
                <strong>{mailboxLabel(box)}</strong>
                <small>{box.email}</small>
              </span>
            </button>)}
          </div>
        </div>

        {activeMailbox&&<div className="mailbox-settings-card">
          <div><Settings2 size={15}/><strong>Mailbox settings</strong></div>
          <span>Customer acknowledgement</span>
          <button type="button" className={Number(activeMailbox.customer_auto_ack_enabled)?'enabled':''} onClick={toggleAutoAck}>
            {Number(activeMailbox.customer_auto_ack_enabled)?'Enabled':'Disabled'}
          </button>
        </div>}
      </aside>

      <section className="mail-message-pane" aria-label="Email messages">
        <div className="mail-list-toolbar">
          <label className="mail-search govuk-label">
            <Search size={16}/>
            <input className="govuk-input" value={query} onChange={event=>setQuery(event.target.value)} placeholder="Search mail"/>
          </label>
          <span>{messages.length} message{messages.length===1?'':'s'}</span>
        </div>

        <div className="mail-message-list">
          {messages.length?messages.map(message=>{
            const outboundMessage=message.direction==='outbound';
            const counterpart=outboundMessage
              ?`To ${message.recipients?.[0]?.email||message.recipients?.[0]||'recipient'}`
              :(message.sender_name||message.sender_email||'Unknown sender');
            return <button
              key={message.id}
              className={`mail-message-row ${message.id===selected?'selected':''} ${message.is_read?'':'unread'}`}
              onClick={()=>setSelected(message.id)}
            >
              <span className="mail-message-status">
                {!message.is_read&&<i/>}
                {outboundMessage?<SendHorizontal size={15}/>:message.is_read?<MailOpen size={15}/>:<InboxIcon size={15}/>}
              </span>
              <span className="mail-message-copy">
                <span className="mail-message-topline">
                  <strong>{counterpart}</strong>
                  <time>{dateTime(message.occurred_at)}</time>
                </span>
                <span className="mail-message-subject">{message.subject||'(No subject)'}</span>
                <small>{message.body_preview||'No preview available'}</small>
                <span className="mail-message-meta">
                  <span>{message.mailbox_name||message.mailbox_email}</span>
                  {message.customer_number&&<span>{message.customer_number}</span>}
                  {message.thread_reference&&<span>{message.thread_reference}</span>}
                </span>
              </span>
              <ChevronRight size={15} className="mail-message-chevron"/>
            </button>;
          }):<div className="mail-empty-list">
            <InboxIcon size={28}/>
            <strong>No email found</strong>
            <span>Try another mailbox, folder or search.</span>
          </div>}
        </div>
      </section>

      <section className="mail-reader-pane">
        {selectedMessage
          ?<EmailThreadPanel messageId={selected} onSent={load}/>
          :<div className="mail-reader-empty">
            <span className="mail-reader-empty-icon"><Mail size={28}/></span>
            <strong>Select an email</strong>
            <p className="govuk-body">Choose a message from the list to read the full conversation, reply, reply all or forward it.</p>
          </div>}
      </section>
      </div>
    </section>

    {deleteTarget&&<ManagementDeleteDialog
      title="Remove email from CRM"
      label={deleteTarget.subject||'email message'}
      description="This removes only Customer Operations' local CRM copy of the message. It does not delete the original email from Microsoft 365 / Exchange."
      consequences={[
        'The message will disappear from the CRM Inbox and Customer360 email views.',
        'The original Microsoft 365 mailbox message remains untouched.',
        'If this is the final CRM message in its thread, the empty local thread state is also removed.'
      ]}
      busy={busy}
      close={()=>!busy&&setDeleteTarget(null)}
      confirm={remove}
    />}


    {composeOpen&&<EmailComposeDialog
      mailboxes={mailboxes}
      close={()=>setComposeOpen(false)}
      onSent={async()=>{setComposeOpen(false);await syncNow();}}
    />}
  </div>;
}
